import { datalabHttp, datalabWithTokenHttp } from '@dbl/services/common';

import {
  transformTableInfoDtoToVo,
  transformTableInfoListDtoToVo,
} from './transformers';
import type {
  IColumnDateCheckRequest,
  IGetGameDatabaseRequest,
  IGetTableInfoDto,
  ITableDetailVo,
  ITablePreviewRequest,
  IUpdateTableRequest,
} from './types';

/**
 * 拉取指定数据表字段信息（字段列表、类型等），用于多维分析/数据源配置等场景。
 * 批量拉取指定数据表信息（跨多个 tableId），用于拖拽页/SQL View/数据管理等需要聚合表信息的场景。
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getTableInfo
 * 目前没有任何地方调用 getTableInfo
 */
export const getTableInfo = (tableId = ''): Promise<IGetTableInfoDto> =>
  datalabHttp<IGetTableInfoDto>({
    url: '/api/v1/datalab/getTableInfo',
    method: 'post',
    data: { table_id: tableId },
  });

/**
 * 拉取单表详情（前端 Vo）。
 *
 * HTTP 返回后在边界层执行 {@link transformTableInfoDtoToVo}。
 */
export const getTableInfoVo = async (tableId = ''): Promise<ITableDetailVo> => {
  const dto = await getTableInfo(tableId);
  return transformTableInfoDtoToVo(dto);
};

/**
 * 批量拉取指定数据表信息（跨多个 tableId），用于拖拽页/SQL View/数据管理等需要聚合表信息的场景。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getTableInfoList
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/Multidimensional/Analysis/model.ts`
 * - `src/pages/datalab/pages/Multidimensional/Extraction/model.ts`
 * - `src/pages/datalab/pages/Number/SqlView/components/DataTableDetail.tsx`
 * - `src/pages/datalab/pages/Number/DataManagement/components/DataTableDetail.tsx`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/addSourceSteep2.tsx`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/SqlViewList.tsx`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/HeaderData/SqlAddSource.tsx`
 * - `src/pages/datalab/pages/Multidimensional/Analysis/components/DetailModal/useDragDropPageDetail.tsx`
 */
export const getTableInfoList = (
  tableIds: string[],
  gameCode: string,
  system?: string,
  chartId?: string,
): Promise<IGetTableInfoDto[]> =>
  datalabWithTokenHttp<IGetTableInfoDto[]>({
    url: '/api/v1/datalab/batchGetTableInfo',
    method: 'post',
    data: { table_id: tableIds, system, chart_id: chartId, game_code: gameCode },
  });

/**
 * 批量拉取表详情（前端 Vo 数组）。
 */
export const getTableInfoListVo = async (
  tableIds: string[],
  gameCode: string,
  system?: string,
  chartId?: string,
): Promise<ITableDetailVo[]> => {
  const dtoList = await getTableInfoList(tableIds, gameCode, system, chartId);
  return transformTableInfoListDtoToVo(dtoList);
};

/**
 * 拖拉拽页面字段编辑保存接口，用于字段别名/描述等编辑后写回。
 * 作用：拖拉拽页面字段编辑保存接口，用于字段别名/描述等编辑后写回。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> updateTableField
 * 原始引用（used-by）：
 * - `src/pages/datalab/pages/Multidimensional/Analysis/model.ts`
 * - `src/pages/datalab/pages/Number/DataManagement/TableEdit/index.tsx`
 */
export function updateTableField(params: IUpdateTableRequest) {
  return datalabHttp({
    url: '/api/v1/datalab/table/update',
    method: 'post',
    data: params,
  });
}

/**
 * 作用：预览多表关联后的数据样本，用于拖拽页详情弹窗。
 *
 * 原始实现：`src/pages/datalab/services/analysis.ts` -> getTablePreviewData
 */
export function getTablePreviewData(params: ITablePreviewRequest) {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/table_data_preview',
    method: 'post',
    data: params,
  });
}

/**
 * 数据源选择器：拉取游戏下可选数据库及 Schema 列表。
 * 用于 SQL View 编辑器、模型管理等「库 → Schema → 表」级联选择的第一步。
 *
 * 原始实现：`HeaderData/service.ts` -> getDatabaseSelect
 */
export function getDatabaseSelect(
  params: IGetGameDatabaseRequest,
): Promise<unknown> {
  return datalabHttp({
    url: '/api/v1/datalab/customSql/getGameDatabase',
    method: 'post',
    data: params,
  });
}

/**
 * 字段类型转换校验（如转为 date/time 格式），用于字段编辑场景。
 */
export function columnCheck(
  params: IColumnDateCheckRequest,
): Promise<unknown> {
  return datalabWithTokenHttp({
    url: '/api/v1/datalab/table/column_check',
    method: 'post',
    data: params,
  });
}
